import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { 
  Calendar, MapPin, Clock, Loader2, X, User, BookOpen, 
  CreditCard, Package, CheckCircle, AlertCircle, Users
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  waitlisted: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  completed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const statusIcons = {
  confirmed: CheckCircle,
  pending: Clock,
  waitlisted: Users,
  cancelled: X,
  completed: CheckCircle,
};

export default function EnrollmentsPage() {
  const { api } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null); // enrollment object being confirmed for cancel
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await api.get('/enrollments/');
      // Handle both paginated (Django) and non-paginated responses
      const data = response.data?.results || response.data || [];
      setEnrollments(data);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (enrollment) => {
    // Open the policy-aware confirmation dialog instead of window.confirm.
    // The actual POST happens in confirmCancel after explicit acknowledgement.
    setCancelTarget(enrollment);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.post(`/enrollments/${cancelTarget.id}/cancel/`);
      toast.success('Enrollment cancelled. See next steps emailed / shown for refund eligibility.');
      await fetchEnrollments();
      setCancelTarget(null);
    } catch (error) {
      toast.error('Failed to cancel enrollment');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      // Parse 'YYYY-MM-DD' as a *local* date so a date like '2026-06-08'
      // doesn't drift to Jun 7 in browsers west of UTC. `new Date('2026-06-08')`
      // is interpreted as UTC midnight, which becomes the previous day locally.
      let d;
      const ymd = typeof dateStr === 'string' ? dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
      if (ymd) {
        d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
      } else {
        d = new Date(dateStr);
      }
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatMeetingDays = (days) => {
    if (!days) return 'TBD';
    if (Array.isArray(days)) return days.join(', ');
    return days;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      // Parse HH:MM:SS or HH:MM format
      const parts = timeStr.split(':');
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1] || '00';
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Each Enrollment may cover multiple sessions (Mon AM + Tue AM + ...).
  // Flatten to one card per session so parents see specific dates and times,
  // sorted chronologically by session_date (May 15, 2026 — RSS Fixes May 15
  // Enrollments PDF, issues A/G).
  const sessionCards = [];
  for (const e of enrollments) {
    const sessions = Array.isArray(e.session_details) && e.session_details.length
      ? e.session_details
      : [null]; // non-camp items render as a single card with no session info
    sessions.forEach((s, sessionIdx) => {
      sessionCards.push({ enrollment: e, session: s, sessionIdx });
    });
  }
  sessionCards.sort((a, b) => {
    const da = a.session?.session_date || a.enrollment.section_start_date || '';
    const db = b.session?.session_date || b.enrollment.section_start_date || '';
    return String(da).localeCompare(String(db));
  });

  // Group by status (use the parent enrollment's status — all its sessions share it).
  const activeCards = sessionCards.filter(c => ['confirmed', 'pending'].includes(c.enrollment.status));
  const waitlistedCards = sessionCards.filter(c => c.enrollment.status === 'waitlisted');
  const pastCards = sessionCards.filter(c => ['completed', 'cancelled'].includes(c.enrollment.status));

  // Stat-card counts use the distinct enrollment count, not the per-session card count,
  // so "Active: 1" doesn't become "Active: 5" for a 5-day registration.
  const activeEnrollments = enrollments.filter(e => ['confirmed', 'pending'].includes(e.status));
  const waitlistedEnrollments = enrollments.filter(e => e.status === 'waitlisted');
  const pastEnrollments = enrollments.filter(e => ['completed', 'cancelled'].includes(e.status));

  return (
    <div className="space-y-6" data-testid="enrollments-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">My Enrollments</h1>
        <p className="text-muted-foreground">Track your children's course registrations and class schedules</p>
      </div>

      {/* Summary Stats */}
      {enrollments.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-green-700">{activeEnrollments.length}</p>
                  <p className="text-sm text-green-600">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-700">{waitlistedEnrollments.length}</p>
                  <p className="text-sm text-blue-600">Waitlisted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-gray-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-700">{pastEnrollments.filter(e => e.status === 'completed').length}</p>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-primary">{enrollments.length}</p>
                  <p className="text-sm text-primary/70">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {enrollments.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No enrollments yet</h2>
            <p className="text-muted-foreground mb-4">
              Browse our course catalog to find classes for your children.
            </p>
            <Button onClick={() => window.location.href = '/catalog'}>
              Browse Courses
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Active Enrollments */}
          {activeEnrollments.length > 0 && (
            <div>
              <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Active Enrollments
              </h2>
              <div className="space-y-4">
                {activeCards.map((card, index) => (
                  <EnrollmentCard
                    key={`${card.enrollment.id}-${card.sessionIdx}`}
                    enrollment={card.enrollment}
                    session={card.session}
                    index={index}
                    onCancel={handleCancel}
                    formatDate={formatDate}
                    formatMeetingDays={formatMeetingDays}
                    formatTime={formatTime}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Waitlisted */}
          {waitlistedEnrollments.length > 0 && (
            <div>
              <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Waitlisted
              </h2>
              <div className="space-y-4">
                {waitlistedCards.map((card, index) => (
                  <EnrollmentCard
                    key={`${card.enrollment.id}-${card.sessionIdx}`}
                    enrollment={card.enrollment}
                    session={card.session}
                    index={index}
                    onCancel={handleCancel}
                    formatDate={formatDate}
                    formatMeetingDays={formatMeetingDays}
                    formatTime={formatTime}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Enrollments */}
          {pastEnrollments.length > 0 && (
            <div>
              <h2 className="font-semibold text-lg mb-3 flex items-center gap-2 text-muted-foreground">
                <Clock className="h-5 w-5" />
                Past Enrollments
              </h2>
              <div className="space-y-4">
                {pastCards.map((card, index) => (
                  <EnrollmentCard
                    key={`${card.enrollment.id}-${card.sessionIdx}`}
                    enrollment={card.enrollment}
                    session={card.session}
                    index={index}
                    onCancel={handleCancel}
                    formatDate={formatDate}
                    formatMeetingDays={formatMeetingDays}
                    formatTime={formatTime}
                    isPast
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Cancellation Confirmation Dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Enrollment?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to cancel <strong>{cancelTarget?.learner_name || 'your child'}'s</strong> enrollment in{' '}
                <strong>{cancelTarget?.section_title || 'this course'}</strong>.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm text-amber-900 mb-2">Refund Policy</h4>
                <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                  <li><strong>Before course starts:</strong> Full refund minus $25 processing fee</li>
                  <li><strong>After course starts:</strong> Prorated refund for remaining sessions minus $25 fee</li>
                  <li><strong>After 50% completion:</strong> No refund available</li>
                  <li><strong>STEM Kits:</strong> Non-refundable once shipped</li>
                </ul>
                <p className="text-xs text-amber-700 mt-2">
                  Refunds processed within 5-7 business days to original payment method.
                </p>
              </div>
              
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. You will receive a confirmation email with refund details if applicable.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Enrollment</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel Enrollment'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EnrollmentCard({ enrollment, session, index, onCancel, formatDate, formatMeetingDays, formatTime, isPast = false }) {
  const StatusIcon = statusIcons[enrollment.status] || Clock;

  // Per-session detail when present, otherwise fall back to the legacy
  // section-level fields (after-school + course enrollments still ship that way).
  const sessionTitle = session?.course_title
    || enrollment.section_title
    || enrollment.course_title
    || enrollment.section?.course?.title
    || 'Course';
  const sessionDate = session?.session_date || enrollment.section_start_date;
  const sessionEndDate = session ? null : enrollment.section_end_date;
  const sessionStartTime = session?.start_time
    || enrollment.section_start_time
    || enrollment.start_time
    || enrollment.section?.start_time;
  const sessionEndTime = session?.end_time
    || enrollment.section_end_time
    || enrollment.end_time
    || enrollment.section?.end_time;
  // For camp sessions we already know the weekday; for after-school / courses we
  // fall back to the section's meeting_days list ("Mon, Wed, Fri").
  const dayLabel = session?.day_label
    ? `${session.day_label}${session.session_label ? ` ${session.session_label}` : ''}`
    : formatMeetingDays(enrollment.section_meeting_days || enrollment.meeting_days || enrollment.section?.meeting_days);

  return (
    <Card className={`card-dashboard ${isPast ? 'opacity-70' : ''}`} data-testid={`enrollment-${index}`}>
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          {/* Main Info */}
          <div className="flex-1 space-y-3">
            {/* Camp + Course + Status */}
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-semibold text-lg" data-testid={`enrollment-title-${index}`}>
                  {sessionTitle}
                </h3>
                <Badge className={`${statusColors[enrollment.status]} border`}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                </Badge>
              </div>
              {/* Show the camp/program name as a sub-line only when the session
                  has its own course title (so we don't repeat the same string). */}
              {session?.course_title && enrollment.section_title && enrollment.section_title !== session.course_title && (
                <div className="text-xs text-muted-foreground">{enrollment.section_title}</div>
              )}
            </div>

            {/* Learner Info */}
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">
                {enrollment.learner_name || enrollment.learner?.first_name + ' ' + enrollment.learner?.last_name || 'Student'}
              </span>
              {enrollment.learner?.grade && (
                <Badge variant="outline" className="text-xs">Grade {enrollment.learner.grade}</Badge>
              )}
            </div>

            {/* Schedule Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span data-testid={`enrollment-date-${index}`}>
                  {sessionEndDate && String(sessionDate) !== String(sessionEndDate)
                    ? <>{formatDate(sessionDate)} – {formatDate(sessionEndDate)}</>
                    : formatDate(sessionDate)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span data-testid={`enrollment-time-${index}`}>
                  {dayLabel}
                  {sessionStartTime && (
                    <> | {formatTime(sessionStartTime)} – {formatTime(sessionEndTime)}</>
                  )}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col">
                  {enrollment.section_location_address ? (
                    <>
                      <span className="font-medium">{enrollment.section_location_address.name}</span>
                      {enrollment.section_location_address.address && (
                        <span className="text-xs text-muted-foreground">
                          {enrollment.section_location_address.address}
                          {enrollment.section_location_address.city && `, ${enrollment.section_location_address.city}`}
                          {enrollment.section_location_address.state && `, ${enrollment.section_location_address.state}`}
                          {enrollment.section_location_address.zip_code && ` ${enrollment.section_location_address.zip_code}`}
                        </span>
                      )}
                    </>
                  ) : (
                    <span>{enrollment.section_location || enrollment.location_name || enrollment.section?.location?.name || 'Location TBD'}</span>
                  )}
                </div>
              </div>
              {(enrollment.price_paid || enrollment.payment_amount) && (
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  <span data-testid={`enrollment-paid-${index}`}>${Number(enrollment.price_paid || enrollment.payment_amount).toFixed(2)} paid</span>
                </div>
              )}
            </div>

            {/* Kit Info */}
            {enrollment.kit_purchased && (
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-primary" />
                <span className="text-primary font-medium">STEM Kit included</span>
                {enrollment.kit_shipping_status && (
                  <Badge variant="outline" className="text-xs">
                    {enrollment.kit_shipping_status}
                  </Badge>
                )}
              </div>
            )}

            {/* Waitlist Position */}
            {enrollment.status === 'waitlisted' && enrollment.waitlist_position && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <span className="text-blue-700">
                  Waitlist position: <strong>#{enrollment.waitlist_position}</strong>
                </span>
              </div>
            )}

            {/* Enrollment Date */}
            {enrollment.created_at && (
              <p className="text-xs text-muted-foreground">
                Enrolled on {formatDate(enrollment.created_at)}
              </p>
            )}
          </div>
          
          {/* Actions */}
          {!isPast && enrollment.status === 'confirmed' && (
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => onCancel(enrollment)}
                data-testid={`cancel-enrollment-${index}`}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel Enrollment
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
